import * as Sentry from "@sentry/react";
import React from "react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router";
import type { AuthError } from "@supabase/supabase-js";

export function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn("Sentry DSN not found. Skipping Sentry initialization.");
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.feedbackIntegration({
        colorScheme: "system",
        isAnonymous: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

/**
 * Captures critical Supabase authentication errors to Sentry while filtering out typical user errors (like wrong password).
 * Strictly avoids logging PII.
 */
export function captureAuthError(error: AuthError, context?: Record<string, unknown>) {
  // Ignore expected user errors
  if (error.status === 400 || error.status === 401 || error.status === 403 || error.status === 422) {
    return;
  }

  // Capture all other errors (5xx server errors, 429 rate limits, 0 network errors)
  Sentry.captureException(new Error(`Auth Error: ${error.message}`), {
    tags: {
      auth_error_status: error.status || "unknown",
      auth_error_name: error.name,
      ...context,
    },
    // Explicitly don't add full error object as extra to prevent accidental PII leakage (like email in request config)
  });
}
